/**
 * Nexus Embed Script Loader
 *
 * Client component that conditionally loads the nexus-embed.js script
 * only if it hasn't been loaded yet, preventing multiple initializations.
 */

"use client"

import { useEffect, useState } from "react"
import Script from "next/script"

interface NexusEmbedLoaderProps {
  agentId: string
  accent?: string
  gradient?: string
  title?: string
  welcome?: string
  logo?: string
  launcherLogo?: string
  launcherIconColor?: string
  widgetMode?: "circle" | "pill"
  position?: "left" | "right" | "center" | "inline"
  enableInitialMessages?: boolean
  enableScrollFollowups?: boolean
  showScheduleButton?: boolean
  autoToken?: boolean
  /** Host page font so widget iframe can match (e.g. "var(--font-inter)" or "Inter, sans-serif") */
  fontFamily?: string
}

export function NexusEmbedLoader({
  agentId,
  accent = "#18181b",
  gradient,
  title = "Losi",
  welcome = "Hi, I'm Losi, What can I help with today?",
  logo,
  launcherLogo,
  launcherIconColor = "#ffffff",
  widgetMode = "circle",
  position = "left",
  enableInitialMessages = true,
  enableScrollFollowups = true,
  showScheduleButton = true,
  autoToken = true,
  fontFamily,
}: NexusEmbedLoaderProps) {
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    // Check if widget already exists
    if (typeof window === "undefined") return

    // Check if widget is already initialized
    if (window.LosiNexusWidget) {
      return
    }

    // Check if widget DOM elements already exist
    const existingWidget =
      document.getElementById("losi-morph") ||
      document.getElementById("losi-pill-widget") ||
      document.getElementById("losi-inline-trigger")

    if (existingWidget) {
      return
    }

    // Check if script is already loaded
    const existingScript = document.querySelector(
      'script[src*="nexus-embed.js"]'
    )
    if (existingScript) {
      return
    }

    // Safe to load
    setShouldLoad(true)
  }, [])

  if (!shouldLoad) {
    return null
  }

  return (
    <Script
      // id="nexus-embed-enterprise"
      src="https://losi.ai/nexus-embed.js"
      data-agent-id={agentId}
      data-accent={accent}
      data-gradient={gradient}
      data-title={title}
      data-welcome={welcome}
      data-logo={logo}
      data-launcher-logo={launcherLogo}
      data-launcher-icon-color={launcherIconColor}
      data-widget-mode={widgetMode}
      data-position={position}
      data-enable-initial-messages={enableInitialMessages ? "true" : "false"}
      data-enable-scroll-followups={enableScrollFollowups ? "true" : "false"}
      data-show-schedule-button={showScheduleButton ? "true" : "false"}
      data-auto-token={autoToken ? "true" : "false"}
      {...(fontFamily != null && fontFamily !== "" ? { "data-font-family": fontFamily } : {})}
      strategy="afterInteractive"
      // onLoad={() => {
      //   // Ensure widget is only initialized once
      //   if (typeof window !== "undefined") {
      //     window.LosiNexusWidget = { initialized: true }
      //   }
      // }}
    />
  )
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    LosiNexusWidget?: { initialized: boolean }
  }
}
